/**
 * Crear cobro — formulario editorial.
 *
 * Mantiene 1:1 la lógica de datos (estado, endpoints, modos avanzados del sprint 13).
 * Cambia tokens y presentación: paleta andaluza, Spectral display, Hanken Grotesk,
 * Space Mono para rótulos, segmented Tarjeta/Bizum-style, preview de comisión.
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import toast from 'react-hot-toast';
import { copyFor, GroupBuyType } from '../utils/vertical-copy';
import TopBar from '../components/TopBar';


// Pre-sets de sub-marca · al venir con ?modo=X aplicamos defaults y
// guardamos `office_mode` en custom_fields para que el backend lo trate
// como vertical especifico (Tarjeta de la Peña, Propina, Oficina Facil).
const SUBBRAND_PRESETS: Record<string, {
  type: GroupBuyType;
  title: string;
  modeFlag: string;
  hero: { eyebrow: string; title: string; helper: string };
  defaultAmount?: string;
  hideTypeSelector?: boolean;
  hidePolicies?: boolean;
}> = {
  'coordina': {
    type: 'membership_quota',
    title: 'Caja común',
    modeFlag: 'wallet_pena',  // backend marker · se mantiene por compat de datos
    hero: {
      eyebrow: 'Coordina · saldo común',
      title: 'Abre la caja común de tu grupo.',
      helper: 'Pon nombre, importe sugerido por miembro y comparte el enlace. Después podrás registrar gastos desde el panel.',
    },
    defaultAmount: '20',
    hideTypeSelector: true,
    hidePolicies: true,
  },
  'propina': {
    type: 'group_gift',
    title: 'Bote de propinas',
    modeFlag: 'tip_jar',
    hero: {
      eyebrow: 'Propina · QR para tu mesa',
      title: 'Crea el bote de propinas.',
      helper: 'Imprimes el QR y lo pegas en la mesa o sobre la barra. Las propinas se reparten entre el equipo del turno.',
    },
    defaultAmount: '',
    hideTypeSelector: true,
    hidePolicies: true,
  },
  'oficina': {
    type: 'group_gift',
    title: 'Cobro a cliente',
    modeFlag: 'freelance_invoice',
    hero: {
      eyebrow: 'Oficina Fácil · cobro con factura',
      title: 'Manda un cobro con factura.',
      helper: 'Rellena los datos del cliente y el importe. Cuando pague, le emitimos la factura simplificada y te llega copia.',
    },
    defaultAmount: '',
    hideTypeSelector: true,
    hidePolicies: true,
  },
};


const TYPES: { value: GroupBuyType; label: string; subtitle: string }[] = [
  { value: 'papeleta_sitio', label: 'Papeleta de sitio', subtitle: 'Hermandad · DNI nominativo' },
  { value: 'raffle_share', label: 'Sorteo benéfico', subtitle: 'Papeleta legal · Decreto 325/1988' },
  { value: 'porra', label: 'Porra deportiva', subtitle: 'Liga · Champions · Mundial' },
  { value: 'ticket_share', label: 'Entradas en grupo', subtitle: 'Conciertos y festivales' },
  { value: 'membership_quota', label: 'Cuota de socios', subtitle: 'Asociación · club · peña' },
  { value: 'group_gift', label: 'Regalo conjunto', subtitle: 'Cumple · despedida · jubilación' },
  { value: 'event_crowdfund', label: 'Si llegamos a X, va', subtitle: 'Crowdfunding all-or-nothing' },
];


const POLICIES = [
  {
    value: 'organizer_covers',
    label: 'Yo cubro lo que falte',
    desc: 'Si no llegamos al número esperado, tú pones la diferencia. Recomendado para peñas estables.',
  },
  {
    value: 'refund_all',
    label: 'Devolver el dinero a todos',
    desc: 'Si no llegamos, devolvemos todo automáticamente. Cero riesgo para los que ya pagaron.',
  },
  {
    value: 'cancel_remaining',
    label: 'Quedarse con lo recaudado',
    desc: 'Lo que se haya recaudado se queda. Los que pagaron mantienen su participación.',
  },
  {
    value: 'proceed_anyway',
    label: 'Seguir adelante igual',
    desc: 'Sin condiciones. Si pagan 5, pues 5. Útil para regalos abiertos.',
  },
];


// Comisión por vertical (de MODELO_DE_NEGOCIO.md). Es solo para la preview;
// el cálculo real lo hace el backend en cada liquidación.
const COMMISSION_PCT: Record<string, number> = {
  membership_quota: 1.5,
  papeleta_sitio: 2.0,
  ticket_share: 2.0,
  raffle_share: 2.5,
  group_dinner: 2.5,
  group_gift: 3.0,
  porra: 3.0,
  event_crowdfund: 5.0,
};
function commissionFor(type: string): number {
  return COMMISSION_PCT[type] ?? 2.5;
}
function formatEUR(n: number): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}


export default function GroupBuyCreatePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const modoParam = params.get('modo') || '';
  const preset = SUBBRAND_PRESETS[modoParam] || null;

  const [type, setType] = useState<GroupBuyType>(preset?.type || 'group_gift');
  const [title, setTitle] = useState(preset?.title || '');
  const [orgName, setOrgName] = useState('');
  const [orgEmail, setOrgEmail] = useState('');
  const [orgPhone, setOrgPhone] = useState('');
  const [amount, setAmount] = useState(preset?.defaultAmount || '');
  const [shares, setShares] = useState('');
  const [deadline, setDeadline] = useState('');
  const [onIncomplete, setOnIncomplete] = useState('organizer_covers');
  const [submitting, setSubmitting] = useState(false);

  // Campos especificos sub-marca
  const [tipRecipients, setTipRecipients] = useState('');  // Propina
  const [clientName, setClientName] = useState('');         // Oficina Facil
  const [clientNif, setClientNif] = useState('');           // Oficina Facil
  const [issuerNif, setIssuerNif] = useState('');           // Oficina Facil
  const [issuerAddress, setIssuerAddress] = useState('');   // Oficina Facil
  const [invoiceConcept, setInvoiceConcept] = useState(''); // Oficina Facil (concepto resumen)
  const [ivaRegime, setIvaRegime] = useState<'21' | '10' | '4' | 'exento'>('21');
  const [irpfPct, setIrpfPct] = useState<'0' | '7' | '15'>('0');
  const [invoicePrefix, setInvoicePrefix] = useState('F');  // Oficina Facil
  const [invoiceNotes, setInvoiceNotes] = useState('');     // Oficina Facil
  const [invoiceLines, setInvoiceLines] = useState<{ concept: string; qty: string; unit_price: string }[]>(
    [{ concept: '', qty: '1', unit_price: '' }],
  );

  const [mode, setMode] = useState<'per_person' | 'total'>(
    preset?.modeFlag === 'wallet_pena' ? 'per_person' : 'per_person',
  );
  const [total, setTotal] = useState('');

  const [organizerAlreadyPaid, setOrganizerAlreadyPaid] = useState(false);
  const [paymentMode, setPaymentMode] = useState<'fixed' | 'flexible' | 'uneven'>(
    preset?.modeFlag === 'tip_jar' ? 'flexible' : 'fixed',
  );
  const [flexibleSuggested, setFlexibleSuggested] = useState(
    preset?.modeFlag === 'tip_jar' ? '2' : '',
  );
  const [flexibleMin, setFlexibleMin] = useState(
    preset?.modeFlag === 'tip_jar' ? '1' : '',
  );
  const [luckyDrawEnabled, setLuckyDrawEnabled] = useState(false);
  const [luckyDrawPrize, setLuckyDrawPrize] = useState('');
  const [waitlistEnabled, setWaitlistEnabled] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const c = copyFor(type);

  // ── Cálculo de factura Oficina Fácil ──────────────────────────────
  const isOfficeFacil = preset?.modeFlag === 'freelance_invoice';
  const officeBase = isOfficeFacil
    ? invoiceLines.reduce((acc, l) => {
        const q = parseFloat(l.qty || '0') || 0;
        const u = parseFloat(l.unit_price || '0') || 0;
        return acc + q * u;
      }, 0)
    : 0;
  const officeIvaRate = ivaRegime === 'exento' ? 0 : parseFloat(ivaRegime) / 100;
  const officeIrpfRate = parseFloat(irpfPct) / 100;
  const officeIva = officeBase * officeIvaRate;
  const officeIrpf = officeBase * officeIrpfRate;
  const officeTotal = officeBase + officeIva - officeIrpf;

  const computedPerPerson =
    mode === 'total' && total && shares
      ? parseFloat(total) / parseInt(shares)
      : null;

  const previewTotal =
    mode === 'total' && total
      ? parseFloat(total)
      : mode === 'per_person' && amount && shares
      ? parseFloat(amount) * parseInt(shares)
      : null;
  const commissionPct = commissionFor(type);
  const previewCommission = previewTotal != null ? (previewTotal * commissionPct) / 100 : null;
  const previewNet = previewTotal != null && previewCommission != null ? previewTotal - previewCommission : null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !orgName || !orgEmail) {
      toast.error('Hay campos sin rellenar');
      return;
    }

    let finalAmount: number;
    let finalShares: number | undefined;

    if (isOfficeFacil) {
      if (officeBase <= 0) {
        toast.error('Pon al menos una línea con importe en la factura');
        return;
      }
      if (!issuerNif.trim()) {
        toast.error('Pon tu NIF como emisor');
        return;
      }
      finalAmount = Math.round(officeTotal * 100) / 100;
      finalShares = 1;
    } else if (mode === 'total') {
      if (!total || !shares) {
        toast.error('Pon el total y entre cuántas personas');
        return;
      }
      finalShares = parseInt(shares);
      finalAmount = parseFloat(total) / finalShares;
      if (!isFinite(finalAmount) || finalAmount <= 0) {
        toast.error('La división no sale válida. Revisa los números.');
        return;
      }
      finalAmount = Math.ceil(finalAmount * 100) / 100;
    } else {
      if (!amount) {
        toast.error('Pon el importe que paga cada persona');
        return;
      }
      finalAmount = parseFloat(amount);
      finalShares = shares ? parseInt(shares) : undefined;
    }

    // Marcadores de sub-marca · backend los lee desde custom_fields
    const customFields: Record<string, any> = {};
    if (preset?.modeFlag === 'wallet_pena') {
      customFields.wallet_pena = true;
    } else if (preset?.modeFlag === 'tip_jar') {
      customFields.tip_jar = true;
      if (tipRecipients.trim()) {
        customFields.tip_recipients = tipRecipients
          .split(',')
          .map((r) => r.trim())
          .filter(Boolean);
      }
    } else if (preset?.modeFlag === 'freelance_invoice') {
      customFields.freelance_invoice = true;
      if (issuerNif.trim()) customFields.issuer_nif = issuerNif.trim().toUpperCase();
      if (issuerAddress.trim()) customFields.issuer_address = issuerAddress.trim();
      if (clientName.trim()) customFields.client_name = clientName.trim();
      if (clientNif.trim()) customFields.client_nif = clientNif.trim().toUpperCase();
      if (invoiceConcept.trim()) customFields.invoice_concept = invoiceConcept.trim();
      customFields.iva_regime = ivaRegime;
      customFields.irpf_pct = irpfPct;
      if (invoicePrefix.trim()) customFields.invoice_prefix = invoicePrefix.trim().toUpperCase().slice(0, 6);
      if (invoiceNotes.trim()) customFields.invoice_notes = invoiceNotes.trim();
      // Líneas con concepto / cantidad / precio unitario · solo las que tienen importe
      customFields.invoice_lines = invoiceLines
        .filter((l) => parseFloat(l.unit_price || '0') > 0)
        .map((l) => ({
          concept: l.concept.trim() || invoiceConcept.trim() || 'Servicio',
          qty: parseFloat(l.qty || '1') || 1,
          unit_price: parseFloat(l.unit_price) || 0,
        }));
    }

    setSubmitting(true);
    try {
      const { data } = await api.post('/public/group-buys', {
        type,
        title,
        organizer_name: orgName,
        organizer_email: orgEmail,
        organizer_phone: orgPhone || undefined,
        amount_per_share: paymentMode === 'flexible' ? undefined : finalAmount,
        expected_shares: finalShares,
        deadline: deadline ? new Date(deadline).toISOString() : undefined,
        on_incomplete: onIncomplete,
        custom_fields: Object.keys(customFields).length > 0 ? customFields : undefined,
        organizer_already_paid: organizerAlreadyPaid,
        payment_mode: paymentMode,
        flexible_min_amount: flexibleMin ? parseFloat(flexibleMin) : undefined,
        flexible_suggested_amount: flexibleSuggested ? parseFloat(flexibleSuggested) : undefined,
        lucky_draw_enabled: luckyDrawEnabled,
        lucky_draw_prize: luckyDrawPrize || undefined,
        waitlist_enabled: waitlistEnabled,
      });
      toast.success('Listo, te hemos enviado los enlaces');
      navigate(`/g/${data.slug}/admin?t=${data.admin_token}`);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Algo no ha ido bien. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      <TopBar contextLabel="Crear cobro" rightCta={{ label: '¿Te ayudamos?', to: '/empezar' }} />

      <main className="max-w-3xl mx-auto px-6 pt-12 md:pt-16 pb-16">
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-4">
          · {preset?.hero.eyebrow || 'Crear cobro'}
        </div>
        <h1 className="font-display font-bold text-[36px] md:text-[44px] leading-[1.02] tracking-[-0.02em] text-ink mb-3">
          {preset?.hero.title || 'Cuéntanos qué cobras.'}
        </h1>
        <p className="text-[15px] text-ink-700 max-w-[560px] mb-10">
          {preset?.hero.helper ||
            'Tarda 30 segundos. Sin registro. Te llegan dos enlaces por email — uno público para WhatsApp y otro privado para ti.'}
        </p>

        <form onSubmit={submit} className="space-y-8">
          {/* Campos especificos por sub-marca */}
          {preset?.modeFlag === 'tip_jar' && (
            <Block
              label="Quién recibe la propina"
              helper="Nombres separados por comas. Si lo dejas vacío, todo va al organizador."
            >
              <input
                type="text"
                value={tipRecipients}
                onChange={(e) => setTipRecipients(e.target.value)}
                placeholder="Pepe, Juan, Carmen"
                className="cc-input"
              />
            </Block>
          )}
          {preset?.modeFlag === 'freelance_invoice' && (
            <>
              <Block label="Emisor de la factura · tú">
                <div className="grid md:grid-cols-2 gap-3">
                  <Field label="NIF / CIF" helper="Aparecerá en cabecera de factura">
                    <input
                      type="text"
                      value={issuerNif}
                      onChange={(e) => setIssuerNif(e.target.value)}
                      placeholder="12345678A · B12345678"
                      className="cc-input"
                    />
                  </Field>
                  <Field
                    label="Prefijo serie"
                    helper='Ej. "F" → F-2026-0001'
                  >
                    <input
                      type="text"
                      value={invoicePrefix}
                      onChange={(e) => setInvoicePrefix(e.target.value)}
                      maxLength={6}
                      placeholder="F"
                      className="cc-input"
                    />
                  </Field>
                </div>
                <div className="mt-3">
                  <Field
                    label={
                      <>
                        Dirección fiscal{' '}
                        <span className="text-ink-400 font-normal">(opcional)</span>
                      </>
                    }
                    helper="Tres líneas máximo · saltos con Enter"
                  >
                    <textarea
                      value={issuerAddress}
                      onChange={(e) => setIssuerAddress(e.target.value)}
                      rows={3}
                      placeholder={'Calle X 12, 3º B\n28020 Madrid\nEspaña'}
                      className="cc-input"
                      style={{ resize: 'vertical', minHeight: '74px' }}
                    />
                  </Field>
                </div>
              </Block>

              <Block label="Cliente · destinatario">
                <div className="grid md:grid-cols-2 gap-3">
                  <Field
                    label={
                      <>
                        Nombre / razón social{' '}
                        <span className="text-ink-400 font-normal">(opcional)</span>
                      </>
                    }
                    helper="Si lo dejas vacío → factura simplificada al consumidor final"
                  >
                    <input
                      type="text"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Razón social del cliente"
                      className="cc-input"
                    />
                  </Field>
                  <Field
                    label={
                      <>
                        NIF del cliente{' '}
                        <span className="text-ink-400 font-normal">(opcional)</span>
                      </>
                    }
                    helper="Con NIF → factura completa. Sin NIF → simplificada."
                  >
                    <input
                      type="text"
                      value={clientNif}
                      onChange={(e) => setClientNif(e.target.value)}
                      placeholder="12345678A"
                      className="cc-input"
                    />
                  </Field>
                </div>
              </Block>

              <Block
                label="Líneas de la factura"
                helper="Lo que figurará en la factura. Pon una línea por concepto."
              >
                <div className="space-y-2">
                  {invoiceLines.map((l, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-start">
                      <input
                        type="text"
                        value={l.concept}
                        onChange={(e) => {
                          const next = [...invoiceLines];
                          next[i].concept = e.target.value;
                          setInvoiceLines(next);
                        }}
                        placeholder="Concepto"
                        className="cc-input col-span-7"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={l.qty}
                        onChange={(e) => {
                          const next = [...invoiceLines];
                          next[i].qty = e.target.value;
                          setInvoiceLines(next);
                        }}
                        placeholder="Cant."
                        className="cc-input col-span-2 text-right"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={l.unit_price}
                        onChange={(e) => {
                          const next = [...invoiceLines];
                          next[i].unit_price = e.target.value;
                          setInvoiceLines(next);
                        }}
                        placeholder="Precio €"
                        className="cc-input col-span-2 text-right"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (invoiceLines.length === 1) {
                            setInvoiceLines([{ concept: '', qty: '1', unit_price: '' }]);
                          } else {
                            setInvoiceLines(invoiceLines.filter((_, idx) => idx !== i));
                          }
                        }}
                        className="col-span-1 h-[44px] rounded-xl border-[1.5px] border-line-strong text-ink-500 hover:text-brick hover:border-brick transition"
                        aria-label="Quitar línea"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setInvoiceLines([...invoiceLines, { concept: '', qty: '1', unit_price: '' }])
                    }
                    className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand hover:text-brand-700 transition"
                  >
                    + Añadir línea
                  </button>
                </div>
              </Block>

              <Block
                label="Régimen de IVA"
                helper="Calculamos base, IVA y retención automáticos."
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {(['21', '10', '4', 'exento'] as const).map((reg) => (
                    <Seg
                      key={reg}
                      selected={ivaRegime === reg}
                      onClick={() => setIvaRegime(reg)}
                      title={reg === 'exento' ? 'Exento' : `${reg} %`}
                      subtitle={
                        reg === 'exento'
                          ? 'art. 20 Ley IVA'
                          : reg === '21'
                          ? 'General'
                          : reg === '10'
                          ? 'Reducido'
                          : 'Superreducido'
                      }
                    />
                  ))}
                </div>
              </Block>

              <Block
                label="Retención IRPF"
                helper="0 % consumidor final · 7 % autónomos en primeros 2 años de alta · 15 % general B2B."
              >
                <div className="grid grid-cols-3 gap-2">
                  {(['0', '7', '15'] as const).map((p) => (
                    <Seg
                      key={p}
                      selected={irpfPct === p}
                      onClick={() => setIrpfPct(p)}
                      title={`${p} %`}
                      subtitle={
                        p === '0' ? 'Sin retención' : p === '7' ? 'Reducida' : 'General'
                      }
                    />
                  ))}
                </div>
              </Block>

              {officeBase > 0 && (
                <div className="bg-paper border border-line shadow-card rounded-card p-6">
                  <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-3">
                    · Desglose factura · en vivo
                  </div>
                  <div className="grid grid-cols-2 gap-y-1.5 text-[14px]">
                    <span className="text-ink-500">Base imponible</span>
                    <span className="text-right font-display font-semibold text-ink tabular-nums">
                      {formatEUR(officeBase)}
                    </span>
                    {ivaRegime !== 'exento' && (
                      <>
                        <span className="text-ink-500">IVA {ivaRegime}%</span>
                        <span className="text-right font-display font-semibold text-ink tabular-nums">
                          {formatEUR(officeIva)}
                        </span>
                      </>
                    )}
                    {irpfPct !== '0' && (
                      <>
                        <span className="text-ink-500">Retención IRPF {irpfPct}%</span>
                        <span className="text-right font-display font-semibold text-terra-700 tabular-nums">
                          − {formatEUR(officeIrpf)}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="mt-4 pt-4 border-t border-line-soft flex items-baseline justify-between">
                    <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500">
                      · Total a cobrar
                    </span>
                    <span className="font-display font-bold text-[26px] text-olive-700 tracking-[-0.02em] leading-none">
                      {formatEUR(officeTotal)}
                    </span>
                  </div>
                </div>
              )}

              <Block
                label={
                  <>
                    Notas en la factura{' '}
                    <span className="text-ink-400 font-normal">(opcional)</span>
                  </>
                }
                helper="Ej. número de pedido del cliente, hora de prestación, etc."
              >
                <textarea
                  value={invoiceNotes}
                  onChange={(e) => setInvoiceNotes(e.target.value)}
                  rows={2}
                  placeholder="Trabajo correspondiente al pedido PO-2026-0481"
                  className="cc-input"
                  style={{ resize: 'vertical', minHeight: '58px' }}
                />
              </Block>
            </>
          )}

          {/* Tipo de cobro · oculto cuando hay sub-marca */}
          {!preset?.hideTypeSelector && (
          <Block label="Tipo de cobro" helper={c.shortDesc}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TYPES.map((t) => (
                <Chip
                  key={t.value}
                  selected={type === t.value}
                  onClick={() => setType(t.value)}
                  title={t.label}
                  subtitle={t.subtitle}
                />
              ))}
            </div>
          </Block>
          )}

          {/* Título */}
          <Block label="¿Cómo se llama?" helper="El nombre que verán los que paguen">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder='Ej. "Papeleta Macarena 2027" · "Porra Champions Final"'
              className="cc-input"
            />
          </Block>

          {/* Organizador */}
          <Block label="Tú">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Tu nombre" helper="Como te conocen en el grupo">
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  required
                  placeholder="Pepe García"
                  className="cc-input"
                />
              </Field>
              <Field label="Tu email" helper="Aquí te llegan los enlaces">
                <input
                  type="email"
                  value={orgEmail}
                  onChange={(e) => setOrgEmail(e.target.value)}
                  required
                  placeholder="tucorreo@ejemplo.es"
                  className="cc-input"
                />
              </Field>
            </div>
            <div className="mt-3">
              <Field
                label={
                  <>
                    Tu teléfono <span className="text-ink-400 font-normal">(opcional)</span>
                  </>
                }
              >
                <input
                  type="tel"
                  value={orgPhone}
                  onChange={(e) => setOrgPhone(e.target.value)}
                  placeholder="+34 …"
                  className="cc-input"
                />
              </Field>
            </div>
          </Block>

          {/* Modo de cobro · oculto en Oficina Fácil (importe lo da la factura) */}
          {!isOfficeFacil && (
          <Block label="¿Cómo lo cobras?">
            <div className="grid grid-cols-2 gap-2 mb-4">
              <Seg
                selected={mode === 'per_person'}
                onClick={() => setMode('per_person')}
                title="Cada uno paga X"
                subtitle="Sé el importe por persona"
              />
              <Seg
                selected={mode === 'total'}
                onClick={() => setMode('total')}
                title="Divide el total"
                subtitle="Tengo el total y N personas"
              />
            </div>

            {mode === 'per_person' ? (
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Importe en €" helper={c.amountHint}>
                  <input
                    type="number"
                    step="0.01"
                    min="0.5"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required={mode === 'per_person'}
                    placeholder="25"
                    className="cc-input"
                  />
                </Field>
                <Field
                  label={
                    <>
                      Cuántos <span className="text-ink-400 font-normal">(opcional)</span>
                    </>
                  }
                  helper={c.expectedHint}
                >
                  <input
                    type="number"
                    min="1"
                    value={shares}
                    onChange={(e) => setShares(e.target.value)}
                    placeholder="18"
                    className="cc-input"
                  />
                </Field>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Total a recaudar (€)" helper="El total de la cuenta">
                  <input
                    type="number"
                    step="0.01"
                    min="0.5"
                    value={total}
                    onChange={(e) => setTotal(e.target.value)}
                    required={mode === 'total'}
                    placeholder="87.50"
                    className="cc-input"
                  />
                </Field>
                <Field label="Entre cuántas personas" helper={c.expectedHint}>
                  <input
                    type="number"
                    min="2"
                    value={shares}
                    onChange={(e) => setShares(e.target.value)}
                    required={mode === 'total'}
                    placeholder="5"
                    className="cc-input"
                  />
                </Field>
              </div>
            )}
          </Block>
          )}

          {/* Preview en vivo + comisión */}
          {previewTotal != null && previewTotal > 0 && (
            <div className="bg-paper border border-line shadow-card rounded-card p-6">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-3">
                · Resumen del dinero
              </div>
              {mode === 'total' && computedPerPerson != null && (
                <p className="text-[14.5px] text-ink-700 mb-4">
                  Cada {c.payerSingular} pagará{' '}
                  <strong className="text-ink font-display text-[16px]">
                    {formatEUR(Math.ceil(computedPerPerson * 100) / 100)}
                  </strong>
                  .
                </p>
              )}
              <div className="grid grid-cols-3 gap-3 text-[13px]">
                <Kpi label="Recaudaréis" value={formatEUR(previewTotal)} tone="ink" />
                <Kpi
                  label={`Comisión ${commissionPct}%`}
                  value={formatEUR(previewCommission ?? 0)}
                  tone="terra"
                />
                <Kpi
                  label="Recibirás"
                  value={formatEUR(previewNet ?? 0)}
                  tone="olive"
                />
              </div>
              <p className="mt-3 text-[12px] text-ink-500">
                La comisión sale de lo recaudado · el pagador nunca la ve.
              </p>
            </div>
          )}

          {/* Deadline */}
          <Block
            label={
              <>
                Hasta cuándo aceptas pagos{' '}
                <span className="text-ink-400 font-normal">(opcional)</span>
              </>
            }
            helper="Sin fecha = sigue abierto hasta que tú cierres"
          >
            <input
              type="datetime-local"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="cc-input"
            />
          </Block>

          {/* Política fallo · oculto en sub-marcas */}
          {!preset?.hidePolicies && (
          <Block label="Si no llegáis al objetivo…">
            <div className="space-y-2">
              {POLICIES.map((p) => {
                const sel = onIncomplete === p.value;
                return (
                  <label
                    key={p.value}
                    className={
                      sel
                        ? 'block border-[1.5px] border-brand bg-brand-50 rounded-xl p-4 cursor-pointer transition'
                        : 'block border-[1.5px] border-line-strong bg-paper rounded-xl p-4 cursor-pointer hover:border-brand transition'
                    }
                  >
                    <div className="flex items-baseline gap-2">
                      <input
                        type="radio"
                        name="policy"
                        value={p.value}
                        checked={sel}
                        onChange={() => setOnIncomplete(p.value)}
                        className="accent-brand"
                      />
                      <span
                        className={
                          sel
                            ? 'font-display font-semibold text-[16px] text-brand-700 tracking-[-0.01em]'
                            : 'font-display font-semibold text-[16px] text-ink tracking-[-0.01em]'
                        }
                      >
                        {p.label}
                      </span>
                    </div>
                    <p className="mt-1 pl-6 text-[13.5px] text-ink-700 leading-relaxed">
                      {p.desc}
                    </p>
                  </label>
                );
              })}
            </div>
          </Block>
          )}

          {/* Opciones avanzadas */}
          <div className="border-t border-line-soft pt-6">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand hover:text-brand-700 transition"
            >
              {showAdvanced ? '— Cerrar opciones avanzadas' : '+ Opciones avanzadas'}
            </button>
          </div>

          {showAdvanced && (
            <div className="space-y-6 border-l-2 border-line-strong pl-6 py-2">
              <Check
                checked={organizerAlreadyPaid}
                onChange={setOrganizerAlreadyPaid}
                title="Ya adelanté el dinero — esto es para que me devuelvan"
                subtitle='El email a cada uno dirá "te toca devolver tu parte de XXX"'
              />

              <div>
                <label className="cc-label">¿Importe igual o flexible?</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Seg
                    selected={paymentMode === 'fixed'}
                    onClick={() => setPaymentMode('fixed')}
                    title="Todos lo mismo"
                    subtitle="Importe fijo"
                  />
                  <Seg
                    selected={paymentMode === 'flexible'}
                    onClick={() => setPaymentMode('flexible')}
                    title="Pon lo que quieras"
                    subtitle="Cada uno elige"
                  />
                  <Seg
                    selected={paymentMode === 'uneven'}
                    onClick={() => setPaymentMode('uneven')}
                    title="Importe por persona"
                    subtitle="Lista nominal"
                  />
                </div>
              </div>

              {paymentMode === 'flexible' && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Sugerido (€)">
                    <input
                      type="number"
                      step="0.5"
                      value={flexibleSuggested}
                      onChange={(e) => setFlexibleSuggested(e.target.value)}
                      placeholder="10"
                      className="cc-input"
                    />
                  </Field>
                  <Field label="Mínimo (€)">
                    <input
                      type="number"
                      step="0.5"
                      value={flexibleMin}
                      onChange={(e) => setFlexibleMin(e.target.value)}
                      placeholder="1"
                      className="cc-input"
                    />
                  </Field>
                </div>
              )}

              {paymentMode === 'uneven' && (
                <div className="bg-brand-50 border border-[#CBD9E2] rounded-xl p-4 text-[13.5px] text-brand-700">
                  Después de crear el cobro, vas al panel y añades a cada persona con
                  su importe. También puedes subir foto del ticket y la IA lo calcula
                  solo.
                </div>
              )}

              <Check
                checked={waitlistEnabled}
                onChange={setWaitlistEnabled}
                title="Lista de espera"
                subtitle="Si alguien se cae, entra el siguiente de la cola"
              />

              <Check
                checked={luckyDrawEnabled}
                onChange={setLuckyDrawEnabled}
                title="Sorteo entre los que pagan"
                subtitle="Al cerrarse, sorteamos un premio entre los pagadores"
              />
              {luckyDrawEnabled && (
                <input
                  type="text"
                  value={luckyDrawPrize}
                  onChange={(e) => setLuckyDrawPrize(e.target.value)}
                  placeholder='¿Qué se sortea? Ej. "Una cena para 2"'
                  className="cc-input"
                />
              )}
            </div>
          )}

          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-6 py-4 rounded-xl bg-brand text-paper font-semibold text-[15.5px] shadow-btn hover:bg-brand-700 transition disabled:bg-[#EFE8DC] disabled:text-[#B2A89A] disabled:shadow-none disabled:cursor-not-allowed"
            >
              {submitting ? 'Un momento…' : 'Crear y mandarme los enlaces →'}
            </button>
            <p className="mt-3 text-[12.5px] text-ink-500 text-center">
              Recibirás <strong className="text-ink-700">dos enlaces</strong> por
              email · el público para WhatsApp y el privado para ti.
            </p>
          </div>
        </form>
      </main>

      <Style />
    </div>
  );
}


function Header() {
  return (
    <header className="border-b border-line bg-paper">
      <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3"
          aria-label="Pagafácil · ir al inicio"
        >
          <Mark />
          <span className="font-display font-bold text-[17px] text-ink tracking-[-0.01em]">
            Pagafácil
          </span>
        </Link>
        <Link
          to="/mis-cobros"
          className="hidden md:inline-block text-sm text-ink-700 hover:text-ink"
        >
          Mis cobros
        </Link>
      </div>
    </header>
  );
}


function Mark({ size = 22 }: { size?: number }) {
  return (
    <div
      className="rounded-full"
      style={{
        width: size,
        height: size,
        background:
          'conic-gradient(#1C5D82 0 33%, #C2683E 0 66%, #5E7B3E 0 100%)',
        boxShadow: 'inset 0 0 0 4px #FFFFFF',
      }}
      aria-hidden
    />
  );
}


function Block({
  label,
  helper,
  children,
}: {
  label: React.ReactNode;
  helper?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-2">
        · {label}
      </div>
      {children}
      {helper && (
        <p className="mt-2 text-[12.5px] text-ink-500 italic">{helper}</p>
      )}
    </section>
  );
}


function Field({
  label,
  helper,
  children,
}: {
  label: React.ReactNode;
  helper?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="cc-label">{label}</label>
      {children}
      {helper && <p className="cc-helper">{helper}</p>}
    </div>
  );
}


function Chip({
  selected,
  onClick,
  title,
  subtitle,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        selected
          ? 'text-left p-4 rounded-xl border-[1.5px] border-brand bg-brand-50 transition'
          : 'text-left p-4 rounded-xl border-[1.5px] border-line-strong bg-paper hover:border-brand transition'
      }
    >
      <div
        className={
          selected
            ? 'font-display font-semibold text-[15.5px] text-brand-700 tracking-[-0.01em]'
            : 'font-display font-semibold text-[15.5px] text-ink tracking-[-0.01em]'
        }
      >
        {title}
      </div>
      <div className="text-[12.5px] text-ink-500 mt-0.5">{subtitle}</div>
    </button>
  );
}


function Seg({
  selected,
  onClick,
  title,
  subtitle,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  subtitle?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        selected
          ? 'text-left px-3 py-2.5 rounded-xl border-[1.5px] border-brand bg-brand-50 transition'
          : 'text-left px-3 py-2.5 rounded-xl border-[1.5px] border-line-strong bg-paper hover:border-brand transition'
      }
    >
      <div
        className={
          selected
            ? 'font-semibold text-[13.5px] text-brand-700'
            : 'font-semibold text-[13.5px] text-ink'
        }
      >
        {title}
      </div>
      {subtitle && (
        <div className="text-[12px] text-ink-500 mt-0.5">{subtitle}</div>
      )}
    </button>
  );
}


function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'ink' | 'olive' | 'terra';
}) {
  const valueClass =
    tone === 'olive'
      ? 'text-olive-700'
      : tone === 'terra'
      ? 'text-terra-700'
      : 'text-ink';
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-500 mb-1">
        {label}
      </div>
      <div
        className={`font-display font-semibold text-[20px] leading-none tracking-[-0.01em] ${valueClass}`}
      >
        {value}
      </div>
    </div>
  );
}


function Check({
  checked,
  onChange,
  title,
  subtitle,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  title: string;
  subtitle: string;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-4 h-4 accent-brand"
      />
      <div>
        <div className="font-display font-semibold text-[15px] text-ink tracking-[-0.01em]">
          {title}
        </div>
        <div className="text-[13px] text-ink-700">{subtitle}</div>
      </div>
    </label>
  );
}


function Style() {
  return (
    <style>{`
      .cc-input{
        width:100%;
        padding:13px 15px;
        border:1.5px solid #DDD3C4;
        border-radius:11px;
        background:#FFFFFF;
        font-family:inherit;
        font-size:14.5px;
        color:#26201A;
        outline:none;
        transition:border-color .15s ease;
      }
      .cc-input:focus{ border-color:#1C5D82; }
      .cc-input::placeholder{ color:#A89D8E; }
      .cc-label{
        display:block;
        font-family:"Hanken Grotesk",sans-serif;
        font-size:12.5px;
        font-weight:700;
        letter-spacing:.02em;
        color:#4A4239;
        margin-bottom:7px;
      }
      .cc-helper{
        margin-top:6px;
        font-size:12px;
        color:#8A8073;
        line-height:1.4;
      }
    `}</style>
  );
}
